import { Stack } from "@mui/material";
import { useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import ClientProfileDetailsSection from "./ClientProfileDetailsSection";
import ClientProfileEmailAccountsSection from "./ClientProfileEmailAccountsSection";
import ClientProfileExcelAddinSection from "./ClientProfileExcelAddinSection";
import ClientProfileProductConfirmDialog from "./ClientProfileProductConfirmDialog";
import ClientProfileProductsSection from "./ClientProfileProductsSection";
import ClientProfilesSelectorPanel from "./ClientProfilesSelectorPanel";
import { clientProfilesDataSource } from "./clientProfilesDataSource";
import type { ToggleableClientProductKey } from "./clientProfilesMapping";
import {
  buildClientProfileViewModel,
  filterClientProfiles,
  getOrganizations,
  groupFilteredClients,
  isToggleableProduct,
} from "./clientProfilesMapping";
import type { ClientTypeFilter, PendingProductAction } from "./clientProfilesTypes";

const ClientProfilesContent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [clientTypeFilter, setClientTypeFilter] = useState<ClientTypeFilter>("All");
  const [organizationScope, setOrganizationScope] = useState("");
  const [selectedClientCode, setSelectedClientCode] = useState("");
  const [isUpdatingProductState, setIsUpdatingProductState] = useState(false);
  const [pendingProductAction, setPendingProductAction] = useState<PendingProductAction | undefined>();
  const [lastProductConfirmationByClient, setLastProductConfirmationByClient] = useState<Record<string, string>>({});
  const [productUpdateErrorByClient, setProductUpdateErrorByClient] = useState<Record<string, string>>({});

  const [
    allClientProfiles,
    fetchClientProfilesError,
    { setData: setAllClientProfiles, isFetching: isFetchingClientProfiles },
  ] = useFetch(clientProfilesDataSource.getClientProfiles);

  const clientProfiles = useMemo(() => allClientProfiles ?? [], [allClientProfiles]);

  const organizations = useMemo(() => getOrganizations(clientProfiles), [clientProfiles]);

  const clientsByCode = useMemo(
    () => new Map(clientProfiles.map((client) => [client.clientCode, client])),
    [clientProfiles]
  );

  const filteredClients = useMemo(
    () => filterClientProfiles(clientProfiles, clientsByCode, searchTerm, organizationScope, clientTypeFilter),
    [clientProfiles, clientTypeFilter, clientsByCode, organizationScope, searchTerm]
  );

  const groupedFilteredClients = useMemo(
    () => groupFilteredClients(filteredClients, organizations, organizationScope),
    [filteredClients, organizationScope, organizations]
  );

  const selectedClient = selectedClientCode ? clientsByCode.get(selectedClientCode) : undefined;
  const selectedClientViewModel = useMemo(() => {
    return selectedClient ? buildClientProfileViewModel(selectedClient, clientProfiles) : undefined;
  }, [clientProfiles, selectedClient]);

  const configurableProducts = (selectedClientViewModel?.products ?? []).filter(isToggleableProduct);

  const lastProductConfirmation = selectedClientViewModel
    ? lastProductConfirmationByClient[selectedClientViewModel.clientCode]
    : undefined;
  const productUpdateError = selectedClientViewModel
    ? productUpdateErrorByClient[selectedClientViewModel.clientCode]
    : undefined;

  const handleSourceTemplateSaved = (clientCode: string, sourceTemplateClientCode: string | undefined) => {
    setAllClientProfiles((currentProfiles) =>
      currentProfiles?.map((profile) =>
        profile.clientCode === clientCode ? { ...profile, sourceTemplateClientCode } : profile
      )
    );
  };

  const handleRequestProductStateChange = (
    productKey: ToggleableClientProductKey,
    productName: string,
    enabled: boolean
  ) => {
    setPendingProductAction({
      clientCode: selectedClientViewModel?.clientCode ?? "",
      key: productKey,
      name: productName,
      enabled,
    });
  };

  const handleChangeExcelAddinUseBcReportGenerationViaSession = async (enabled: boolean) => {
    if (!selectedClientViewModel) {
      return;
    }

    const selectedClientCodeForUpdate = selectedClientViewModel.clientCode;
    setIsUpdatingProductState(true);
    setProductUpdateErrorByClient((previousState) => ({
      ...previousState,
      [selectedClientCodeForUpdate]: "",
    }));
    setLastProductConfirmationByClient((previousState) => ({
      ...previousState,
      [selectedClientCodeForUpdate]: "",
    }));

    const updateResponse = await clientProfilesDataSource.updateExcelAddinUseBcReportGenerationViaSession({
      clientCode: selectedClientCodeForUpdate,
      enabled,
    });

    if (!updateResponse.success) {
      setProductUpdateErrorByClient((previousState) => ({
        ...previousState,
        [selectedClientCodeForUpdate]:
          updateResponse.error?.message ?? "Unable to update BC report generation setting.",
      }));
      setIsUpdatingProductState(false);
      return;
    }

    setAllClientProfiles((currentProfiles) => {
      if (!currentProfiles) {
        return currentProfiles;
      }

      return currentProfiles.map((profile) => {
        if (profile.clientCode !== selectedClientCodeForUpdate) {
          return profile;
        }

        return {
          ...profile,
          applications: {
            ...profile.applications,
            excelAddin: {
              ...profile.applications.excelAddin,
              useBcReportGenerationViaSession: enabled,
            },
          },
        };
      });
    });

    setLastProductConfirmationByClient((previousState) => ({
      ...previousState,
      [selectedClientCodeForUpdate]: enabled
        ? "BC report generation via session has been enabled."
        : "BC report generation via session has been disabled.",
    }));

    setIsUpdatingProductState(false);
  };

  const handleConfirmProductStateChange = async () => {
    if (!pendingProductAction) {
      return;
    }

    const selectedClientCodeForUpdate = pendingProductAction.clientCode;
    const pendingActionForUpdate = pendingProductAction;

    setIsUpdatingProductState(true);
    setProductUpdateErrorByClient((previousState) => ({
      ...previousState,
      [selectedClientCodeForUpdate]: "",
    }));
    setLastProductConfirmationByClient((previousState) => ({
      ...previousState,
      [selectedClientCodeForUpdate]: "",
    }));

    const updateResponse = await clientProfilesDataSource.updateClientProductState({
      clientCode: selectedClientCodeForUpdate,
      productKey: pendingActionForUpdate.key,
      enabled: pendingActionForUpdate.enabled,
    });

    if (!updateResponse.success) {
      setProductUpdateErrorByClient((previousState) => ({
        ...previousState,
        [selectedClientCodeForUpdate]: updateResponse.error?.message ?? "Unable to update product state.",
      }));
      setPendingProductAction(undefined);
      setIsUpdatingProductState(false);
      return;
    }

    setAllClientProfiles((currentProfiles) => {
      if (!currentProfiles) {
        return currentProfiles;
      }

      return currentProfiles.map((profile) => {
        if (profile.clientCode !== selectedClientCodeForUpdate) {
          return profile;
        }

        const appKey = pendingActionForUpdate.key;
        return {
          ...profile,
          applications: {
            ...profile.applications,
            [appKey]: { ...profile.applications[appKey], enabled: pendingActionForUpdate.enabled },
          },
        };
      });
    });

    setLastProductConfirmationByClient((previousState) => ({
      ...previousState,
      [selectedClientCodeForUpdate]: pendingActionForUpdate.enabled
        ? `${pendingActionForUpdate.name} has been enabled.`
        : `${pendingActionForUpdate.name} has been disabled.`,
    }));

    setPendingProductAction(undefined);
    setIsUpdatingProductState(false);
  };

  if (isFetchingClientProfiles && !allClientProfiles && !fetchClientProfilesError) {
    return <InlineLoader />;
  }

  if (fetchClientProfilesError) {
    logError(fetchClientProfilesError, "[ClientProfilesContent] getClientProfiles");
    return <DataLoadingFailed text={fetchClientProfilesError.message} />;
  }

  return (
    <Stack spacing={3}>
      <ClientProfilesSelectorPanel
        searchTerm={searchTerm}
        clientTypeFilter={clientTypeFilter}
        organizationScope={organizationScope}
        organizations={organizations}
        filteredClients={filteredClients}
        groupedFilteredClients={groupedFilteredClients}
        selectedClientCode={selectedClientCode}
        onSearchTermChange={setSearchTerm}
        onClientTypeFilterChange={setClientTypeFilter}
        onOrganizationScopeChange={setOrganizationScope}
        onSelectClient={setSelectedClientCode}
      />

      {selectedClientViewModel && (
        <Stack spacing={2}>
          <ClientProfileDetailsSection
            selectedClient={selectedClientViewModel}
            onSelectClient={setSelectedClientCode}
            onSourceTemplateSaved={handleSourceTemplateSaved}
          />
          {configurableProducts.length > 0 && (
            <ClientProfileProductsSection
              configurableProducts={configurableProducts}
              isUpdatingProductState={isUpdatingProductState}
              lastProductConfirmation={lastProductConfirmation}
              productUpdateError={productUpdateError}
              onRequestProductStateChange={handleRequestProductStateChange}
            />
          )}
          {selectedClientViewModel.type === "FundAdmin" && (
            <ClientProfileExcelAddinSection
              useBcReportGenerationViaSession={
                selectedClientViewModel.applications.excelAddin.useBcReportGenerationViaSession
              }
              isUpdating={isUpdatingProductState}
              lastConfirmation={lastProductConfirmation}
              updateError={productUpdateError}
              onChangeUseBcReportGenerationViaSession={handleChangeExcelAddinUseBcReportGenerationViaSession}
            />
          )}
          <ClientProfileEmailAccountsSection
            selectedClient={selectedClientViewModel}
            clientsByCode={clientsByCode}
            onSelectClient={setSelectedClientCode}
          />
        </Stack>
      )}

      <ClientProfileProductConfirmDialog
        pendingProductAction={pendingProductAction}
        isUpdatingProductState={isUpdatingProductState}
        onCancel={() => setPendingProductAction(undefined)}
        onConfirm={handleConfirmProductStateChange}
      />
    </Stack>
  );
};

export default ClientProfilesContent;
